import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Send } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommentsModal = ({ open, onClose, postId }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open && postId) {
            fetchComments();
        }
    }, [open, postId]);

    const fetchComments = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/posts/${postId}/comments`);
            setComments(response.data);
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendComment = async () => {
        if (!newComment.trim() || sending) return;

        setSending(true);
        try {
            const response = await axios.post(`${API}/posts/${postId}/comments`, {
                content: newComment.trim()
            });
            setComments(prev => [...prev, response.data]);
            setNewComment('');
        } catch (error) {
            console.error('Error sending comment:', error);
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendComment();
        }
    };

    return (
        <Sheet open={open} onOpenChange={onClose}>
            <SheetContent 
                side="bottom" 
                className="bg-card-bg border-t border-border h-[70vh] rounded-t-3xl"
                data-testid="comments-modal"
            >
                <SheetHeader className="border-b border-border pb-4">
                    <SheetTitle className="text-white font-heading text-xl">
                        Comments ({comments.length})
                    </SheetTitle>
                </SheetHeader>

                <div className="flex flex-col h-[calc(100%-120px)]">
                    {/* Comments List */}
                    <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : comments.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-32 text-white/50">
                                <p>No comments yet</p>
                                <p className="text-sm">Be the first to comment!</p>
                            </div>
                        ) : (
                            comments.map((comment) => (
                                <div 
                                    key={comment.id} 
                                    className="flex gap-3"
                                    data-testid={`comment-${comment.id}`}
                                >
                                    <Avatar className="w-9 h-9 flex-shrink-0">
                                        <AvatarImage src={comment.user_avatar} alt={comment.username} />
                                        <AvatarFallback className="bg-primary/20 text-primary text-sm">
                                            {comment.username?.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-white font-medium text-sm">
                                                {comment.username}
                                            </span>
                                            <span className="text-white/40 text-xs">
                                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                            </span>
                                        </div>
                                        <p className="text-white/80 text-sm mt-0.5">
                                            {comment.content}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Comment Input */}
                    <div className="border-t border-border pt-4 flex gap-2">
                        <Input
                            ref={inputRef}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Add a comment..."
                            className="flex-1 bg-surface border-border text-white placeholder:text-white/40"
                            data-testid="comment-input"
                        />
                        <Button
                            onClick={handleSendComment}
                            disabled={!newComment.trim() || sending}
                            className="bg-primary hover:bg-primary-hover px-4"
                            data-testid="send-comment-button"
                        >
                            <Send className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default CommentsModal;
